import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  InlineError,
  Button,
  DropZone,
  InlineStack,
  Thumbnail,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import { EditIcon } from "@shopify/polaris-icons";

const AddEditUser = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    name: "",
    email: "",
    role: "",
    department: "",
    type: "",
    status: "",
    link_to_user: "",
    country: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/people/${id}`)
      .then(() => navigate(`/admin/people`))
      .catch(console.error);
  };

  const validateFields = () => {
    let hasError = false;
    const newErrors = {};

    // if (!item.name.trim()) {
    //   newErrors.name = "This field is required";
    //   hasError = true;
    // }

    // if (!item.email.trim()) {
    //   newErrors.email = "This field is required";
    //   hasError = true;
    // } else {
    //   // Validate email format
    //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    //   if (!emailRegex.test(item.email)) {
    //     newErrors.email = "Please enter a valid email";
    //     hasError = true;
    //   }
    // }

    // if (!item.role.trim()) {
    //   newErrors.role = "This field is required";
    //   hasError = true;
    // }

    if (!item.department) {
      newErrors.department = "This field is required";
      hasError = true;
    }

    if (!item.type) {
      newErrors.type = "This field is required";
      hasError = true;
    }

    if (!item.status) {
      newErrors.status = "This field is required";
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const [files, setFiles] = useState();
  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => setFiles(acceptedFiles[0]),
    []
  );

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );
  const [imageError, setImageError] = useState("");
  const imageKey = useRef("");

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "person");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setIsImageUploading(false);
        setItem({
          ...item,
          image:
            validImageTypes.indexOf(files[files.length - 1].type) > -1
              ? window.URL.createObjectURL(files[files.length - 1])
              : "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png",
        });
        setImageError("");
      })
      .catch(() => {});
  };
  useEffect(() => {
    axios.get("/departments?all").then((res) => {
      setDepartments(
        res.data.data.map((d) => ({ label: d.name, value: String(d.id) }))
      );
    });
    axios.get("/management/users?").then((res) => {
      console.log("users=", res.data.data.data);
      setUsers(
        res.data.data.data.map((u) => ({
          label: u.first_name + " " + u.last_name,
          value: String(u.id),
        }))
      );
    });
  }, []);

  useEffect(() => {
    if (mode === "edit") {
      setIsSaving(true);
      axios
        .get(`/people/${id}`)
        .then((res) => {
          const data = res.data.data;
          setItem({
            name: data.name || "",
            email: data.email || "",
            role: data.person_role || "",
            image: data.image_url || "",
            department: data.department_id ? String(data.department_id) : "",
            type: data.type ? String(data?.type?.id) : "",
            status: data.status ? String(data?.status?.id) : "",
            link_to_user: data.user_id !== null ? String(data.user_id) : "",
            country: data.country || "",
            status: data.status ? String(data.status.id) : "",
          });
          setIsSaving(false);
        })
        .catch(console.error);
    }
  }, [mode, id]);

  function handleSave() {
    if (!validateFields()) return;

    setIsSaving(true);
    const formData = new FormData();
    if (mode === "edit") formData.append("_method", "PATCH");

    formData.append("name", item.name);
    formData.append("email", item.email);
    formData.append("person_role", item.role);
    formData.append("department_id", item.department);
    formData.append("type", item.type);
    formData.append("status", item.status);
    formData.append("country", item.country);
    if (item.link_to_user !== "") formData.append("user_id", item.link_to_user);
    imageKey.current && formData.append("image", imageKey.current);
    axios
      .post(mode === "new" ? "/people" : `/people/${id}`, formData)
      .then(() => (mode === "new" ? navigate("/admin/people") : toggleActive()))
      .catch((error) => {
        console.error(error.response);
        if (error.response?.data?.message)
          setErrors({ form: error.response.data.message });
      })
      .finally(() => setIsSaving(false));
  }

  const [optionsCountries, setOptionsCountries] = useState([]);
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        ];
        setOptionsCountries(options);
      })
      .catch((err) => console.log(err));
  }, []);
  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} User`}
      backAction={{ onAction: () => navigate("/admin/people") }}
    >
      {isSaving && <Loading />}
      <Card sectioned>
        <FormLayout>
          <TextField
            label="Name"
            value={item.name}
            onChange={(val) => handleChange("name", val)}
            error={errors.name}
          />
          <Select
            label="Country"
            options={optionsCountries}
            onChange={(value) => handleChange("country", value)}
            value={item.country}
            placeholder="Please select"
            error={errors?.country}
            styles={{
              // Fixes the overlapping problem of the compont
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
          />
          <TextField
            label="Email"
            value={item.email}
            onChange={(val) => handleChange("email", val)}
            error={errors.email}
          />
          <TextField
            label="Role"
            value={item.role}
            onChange={(val) => handleChange("role", val)}
            error={errors.role}
          />
          <Select
            label="Department"
            options={departments}
            value={item.department}
            onChange={(val) => handleChange("department", val)}
            error={errors.department}
            placeholder="Please select"
            labelAction={{
              content: "Edit Departments", // 👈 The text shown on the right of the label
              onAction: () => navigate("/admin/people/edit/departments"), // 👈 Button action
              icon: EditIcon,
            }}
          />
          <Select
            label="Type"
            options={[
              { label: "Team", value: "1" },
              { label: "Stakeholder", value: "2" },
            ]}
            value={item.type}
            onChange={(val) => handleChange("type", val)}
            error={errors.type}
            placeholder="Please select"
          />

          <Select
            label="Status"
            options={[
              { label: "Free", value: "1" },
              { label: "Busy", value: "2" },
            ]}
            value={item.status}
            onChange={(val) => handleChange("status", val)}
            error={errors.status}
            placeholder="Please select"
          />
          <Select
            label="Link to Real User"
            options={users}
            value={item.link_to_user}
            onChange={(val) => handleChange("link_to_user", val)}
            placeholder="Please select"
          />
          {errors.form && <InlineError message={errors.form} />}
          <Text as="p" fontWeight="regular">
            Image
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.image ? item.image : ""} />
            </div>
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={imageError} />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && [
            {
              content: "Delete",
              destructive: true,
              onAction: () => setActiveDelete(true),
              loading: isSaving,
            },
          ]
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete User"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          { content: "No", onAction: toggleDeleteModal, loading: isSaving },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this user?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading ? <LoadingScreen /> : null}
    </Page>
  );
};

export default AddEditUser;
